/**
 * 指示書 (Overtime/Holiday Work Directive) - B5 portrait
 */
export default function OvertimePrintSheet({ submission }) {
  const data = submission.form_data || {};
  const employeeNames = data.employee_names || (data.employee_name ? data.employee_name.split('、') : []);
  const hours = data.hours || 0;
  const isChokouChokki = (data.work_type || '') === '直行・直帰';

  // Circle the SELECTED 勤務種別: 早出 / 残業 / 休日出勤 / 深夜残業 circle their own
  // oval; 直行・直帰 circles BOTH 直行 and 直帰 at the top. Only the chosen one is
  // circled by the system (others left blank).
  const workType = data.work_type || '';

  const formatJpDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return `${dt.getFullYear()}年${dt.getMonth() + 1}月${dt.getDate()}日`;
  };
  const formatSlashDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return `${dt.getFullYear()}/${dt.getMonth() + 1}/${dt.getDate()}`;
  };
  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    return `${parseInt(h, 10)}時${m}分`;
  };

  const rows = [];
  for (let i = 0; i < 10; i++) {
    rows.push({
      no: i + 1,
      name: employeeNames[i] || '',
      hours: employeeNames[i] ? (isChokouChokki ? '' : `${hours}H`) : '',
    });
  }

  // Compact oval circle around label - just slightly bigger than the text
  const WorkLabel = ({ active, label }) => (
    <span style={{ position: 'relative', display: 'inline-block', padding: '2px 4px' }}>
      <span style={{ position: 'relative', zIndex: 1, letterSpacing: '0.3em' }}>{label}</span>
      {active && (
        <span
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '110%',
            height: '20px',
            border: '1.5px solid #000',
            borderRadius: '50%',
            pointerEvents: 'none',
          }}
        />
      )}
    </span>
  );

  const cell = { border: '1px solid #000', padding: '2px 4px', fontSize: '10px', textAlign: 'center', verticalAlign: 'middle' };
  const headerCell = { ...cell, fontWeight: 'bold' };

  // Vertical-text flow box
  const FlowBox = ({ children, width = '32px' }) => (
    <div className="vertical-text" style={{
      border: '1.5px solid #000',
      padding: '6px 4px',
      textAlign: 'center',
      width: width,
      minWidth: width,
      height: '90px',
      fontSize: '11px',
      lineHeight: '1.2',
      display: 'inline-block',
      verticalAlign: 'middle',
      boxSizing: 'border-box',
      letterSpacing: '0.1em',
    }}>
      {children}
    </div>
  );

  // Clean horizontal arrow with triangle head
  const Arrow = ({ width = '36px' }) => (
    <div style={{
      display: 'inline-block',
      verticalAlign: 'middle',
      width: width,
      height: '14px',
      position: 'relative',
    }}>
      <div style={{ position: 'absolute', top: '6px', left: 0, right: 0, borderTop: '1.5px solid #000' }}></div>
      <div style={{
        position: 'absolute',
        top: '0',
        right: '-2px',
        width: 0,
        height: 0,
        borderTop: '7px solid transparent',
        borderBottom: '7px solid transparent',
        borderLeft: '10px solid #000',
      }}></div>
    </div>
  );

  // Tall vertical-text box used in flow chain
  const VBox = ({ children, rowSpan, height = '180px' }) => (
    <td
      rowSpan={rowSpan}
      className="vertical-text"
      style={{
        border: '1.8px solid #000',
        padding: '8px 4px',
        textAlign: 'center',
        verticalAlign: 'middle',
        width: '36px',
        minWidth: '36px',
        height: height,
        fontSize: '12px',
        lineHeight: '1.3',
        letterSpacing: '0.1em',
        background: '#fff',
      }}
    >
      {children}
    </td>
  );

  const ArrowCell = ({ width = '36px', colSpan = 1 }) => (
    <td colSpan={colSpan} style={{ textAlign: 'center', verticalAlign: 'middle', padding: '0 4px' }}>
      <Arrow width={width} />
    </td>
  );

  return (
    <div
      className="print-form"
      style={{
        padding: '8mm',
        fontFamily: "'IBM Plex Sans JP', 'Yu Gothic', sans-serif",
        color: '#000',
        background: '#fff',
        border: 'none',
        boxSizing: 'border-box',
      }}
    >
      {/* TOP HEADER */}
      <div style={{ position: 'relative', minHeight: '140px', marginBottom: '8px' }}>
        {/* Date top-right */}
        <div style={{ position: 'absolute', top: 0, right: 0, fontSize: '11px' }}>
          {formatJpDate(data.date)}
        </div>

        {/* Work-type labels on LEFT */}
        <div style={{ position: 'absolute', top: '20px', left: '4px', fontSize: '14px' }}>
          <div style={{ display: 'flex', gap: '40px', marginBottom: '8px' }}>
            <WorkLabel active={workType === '直行・直帰'} label="直　行" />
            <WorkLabel active={workType === '直行・直帰'} label="直　帰" />
          </div>
          <div style={{ display: 'flex', gap: '40px', marginBottom: '8px', alignItems: 'center' }}>
            <WorkLabel active={workType === '早出'} label="早　出" />
            <WorkLabel active={workType === '残業'} label="残　業" />
          </div>
          <div style={{ display: 'flex', gap: '20px', marginBottom: '4px' }}>
            <WorkLabel active={workType === '休日出勤'} label="休日出勤" />
            <WorkLabel active={workType === '深夜残業'} label="深夜残業" />
          </div>
          <div style={{ fontSize: '10px' }}>（○印を付けて下さい。）</div>
        </div>

        {/* Title (centered) */}
        <h1 style={{
          position: 'absolute',
          top: '38px',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '24px',
          fontWeight: 'bold',
          margin: 0,
          letterSpacing: '0.5em',
        }}>
          指　示　書
        </h1>
      </div>

      {/* 工場長／部長 印 - left-aligned, dotted line only under content */}
      <div style={{ marginTop: '20px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <tbody>
            <tr>
              <td style={{ width: '22%' }}></td>
              <td style={{ width: '24%' }}></td>
              <td style={{
                width: '46%',
                fontSize: '14px',
                textAlign: 'left',
                padding: '0',
                borderBottom: '1px solid #000',
                lineHeight: '1.4',
              }}>
                工場長／部長
              </td>
              <td style={{
                width: '8%',
                fontSize: '14px',
                textAlign: 'right',
                padding: '0',
                borderBottom: '1px solid #000',
                lineHeight: '1.4',
              }}>
                印
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 所属部署 [dept] 所属長 印 - same column layout, 所属長 left-aligned to match 工場長 above */}
      <div style={{ marginTop: '20px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <tbody>
            <tr>
              <td style={{
                width: '22%',
                fontSize: '14px',
                textAlign: 'center',
                padding: '0',
                borderBottom: '1px solid #000',
                lineHeight: '1.4',
              }}>
                所属部署
              </td>
              <td style={{
                width: '24%',
                fontSize: '14px',
                textAlign: 'center',
                padding: '0',
                borderBottom: '1px solid #000',
                lineHeight: '1.4',
              }}>
                {data.department || ''}
              </td>
              <td style={{
                width: '46%',
                fontSize: '14px',
                textAlign: 'left',
                padding: '0',
                borderBottom: '1px solid #000',
                lineHeight: '1.4',
              }}>
                所属長
              </td>
              <td style={{
                width: '8%',
                fontSize: '14px',
                textAlign: 'right',
                padding: '0',
                borderBottom: '1px solid #000',
                lineHeight: '1.4',
              }}>
                印
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Date / time row */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '16px' }}>
        <tbody>
          <tr>
            <td style={{ ...cell, width: '34%', fontSize: '13px', padding: '8px 4px' }}>{formatSlashDate(data.date)}</td>
            <td style={{ ...cell, width: '33%', fontSize: '13px', padding: '8px 4px' }}>{formatTime(data.start_time)}より</td>
            <td style={{ ...cell, width: '33%', fontSize: '13px', padding: '8px 4px' }}>{formatTime(data.end_time)}まで</td>
          </tr>
        </tbody>
      </table>

      {/* Main employee table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th rowSpan={2} style={{ ...headerCell, width: '5%' }}>No.</th>
            <th rowSpan={2} style={{ ...headerCell, width: '22%' }}>氏　名</th>
            <th colSpan={2} style={headerCell}>残業時間(H)</th>
            <th rowSpan={2} style={{ ...headerCell, width: '5%' }}>No.</th>
            <th rowSpan={2} style={{ ...headerCell, width: '22%' }}>氏　名</th>
            <th colSpan={2} style={headerCell}>残業時間(H)</th>
          </tr>
          <tr>
            <th style={{ ...headerCell, width: '11%' }}>申請</th>
            <th style={{ ...headerCell, width: '11%' }}>認定</th>
            <th style={{ ...headerCell, width: '11%' }}>申請</th>
            <th style={{ ...headerCell, width: '11%' }}>認定</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.no}>
              <td style={{ ...cell, height: '20px' }}>{row.no}</td>
              <td style={{ ...cell, textAlign: 'left', paddingLeft: '6px' }}>{row.name}</td>
              <td style={cell}>{row.hours}</td>
              <td style={cell}></td>
              <td style={cell}>{row.no}</td>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}></td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 指示内容 box */}
      <div style={{ border: '1px solid #000', marginTop: '8px', padding: '6px 8px', minHeight: '60px' }}>
        <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '4px', textDecoration: 'underline' }}>指示内容</div>
        {(data.instruction_content || '').split('\n').filter(Boolean).map((line, i) => (
          <div key={`${i}-${line}`} style={{ fontSize: '11px', padding: '2px 0', borderBottom: '1px dotted #888' }}>＊ {line}</div>
        ))}
      </div>

      {/* Flow diagram - use exact image from user */}
      <div style={{ marginTop: '14px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        <img
          src="/flow_diagram.png"
          alt="Flow diagram"
          style={{ flex: 1, maxWidth: '78%', height: 'auto' }}
        />

        {/* Right: 人事・東京総務担当 box */}
        <div style={{ marginLeft: 'auto' }}>
          <table style={{ borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td colSpan={2} style={{ border: '1.5px solid #000', padding: '3px 6px', textAlign: 'center', fontSize: '10px', fontWeight: 'bold' }}>
                  人事・東京総務担当
                </td>
              </tr>
              <tr>
                <td style={{ border: '1.5px solid #000', width: '54px', height: '54px' }}></td>
                <td style={{ border: '1.5px solid #000', width: '54px', height: '54px' }}></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: '8px', fontSize: '9px' }}>
        ※但し、フレックス、職場管理の職場については上記の限りではない。
      </div>
      <div style={{ marginTop: '4px', fontSize: '9px', textAlign: 'right' }}>E322HL ALL</div>
    </div>
  );
}
